import { VehicleType } from '@thive/fddm-types';

import { logger } from '@common/logging/logger';
import { UnifiedVehicle } from '@/modules/vehicles';
import {
  FileReferenceResponse,
  UnifiedVehicleDetails,
  ValidAssetType,
} from '@/modules/vehicles/types';

import { IVehicleService } from '@/modules/vehicles/service/IVehicleService';

export class VehicleServiceExceptionDecorator implements IVehicleService {
  private readonly decoree: IVehicleService;

  public constructor(decoree: IVehicleService) {
    this.decoree = decoree;
  }

  public async loadVehicles(): Promise<void> {
    try {
      await this.decoree.loadVehicles();
    } catch (error) {
      this.logError('loadVehicles', error);
    }
  }

  public getVehicle(vehicleId: string): UnifiedVehicle {
    try {
      return this.decoree.getVehicle(vehicleId);
    } catch (error) {
      this.logError('getVehicle', error);
      throw error;
    }
  }

  public async getVehicleJSONAsset(reference: string): Promise<unknown | null> {
    try {
      return await this.decoree.getVehicleJSONAsset(reference);
    } catch (error) {
      this.logError('getVehicleJSONAsset', error);
      return null;
    }
  }

  public async fetchAndUploadAssetToFileApi(
    vehicleId: string,
    projectId: string,
    layoutAlternativeId: string,
    revisionId: string,
    assetType: ValidAssetType,
  ): Promise<FileReferenceResponse | null> {
    try {
      return await this.decoree.fetchAndUploadAssetToFileApi(
        vehicleId,
        projectId,
        layoutAlternativeId,
        revisionId,
        assetType,
      );
    } catch (error) {
      this.logError('fetchAndUploadAssetToFileApi', error);
      return null;
    }
  }

  public async ensureVehicleAssetAvailability(
    vehicleIds: string[],
    projectId: string,
    layoutAlternativeId: string,
    revisionId: string,
    floorplanVehicleTypes?: VehicleType[],
  ): Promise<VehicleType[]> {
    try {
      return await this.decoree.ensureVehicleAssetAvailability(
        vehicleIds,
        projectId,
        layoutAlternativeId,
        revisionId,
        floorplanVehicleTypes,
      );
    } catch (error) {
      this.logError('ensureVehicleAssetAvailability', error);
      return [];
    }
  }

  public async ensureVehicleDetailsAvailability(
    vehicleIds: string[],
    floorplanVehicleTypes?: VehicleType[],
  ): Promise<void> {
    try {
      await this.decoree.ensureVehicleDetailsAvailability(vehicleIds, floorplanVehicleTypes);
    } catch (error) {
      this.logError('ensureVehicleDetailsAvailability', error);
    }
  }

  public async reconcileEnabledVehicleTypes(
    vehicleIds: string[],
    projectId: string,
    layoutAlternativeId: string,
    revisionId: string,
    addNewVehiclesToHighways = false,
  ): Promise<VehicleType[]> {
    try {
      return await this.decoree.reconcileEnabledVehicleTypes(
        vehicleIds,
        projectId,
        layoutAlternativeId,
        revisionId,
        addNewVehiclesToHighways,
      );
    } catch (error) {
      this.logError('reconcileEnabledVehicleTypes', error);
      return [];
    }
  }

  public cleanUpRedundantTypes(): void {
    try {
      this.decoree.cleanUpRedundantTypes();
    } catch (error) {
      this.logError('cleanUpRedundantTypes', error);
    }
  }

  public setVehicleDetails(vehicleDetails: UnifiedVehicleDetails[]): void {
    try {
      this.decoree.setVehicleDetails(vehicleDetails);
    } catch (error) {
      this.logError('setVehicleDetails', error);
    }
  }

  public removeVehicleDetails(vehicleIds: string[]): void {
    try {
      this.decoree.removeVehicleDetails(vehicleIds);
    } catch (error) {
      this.logError('removeVehicleDetails', error);
    }
  }

  public async getVehicleTypeFiles(vehicleTypes: VehicleType[]): Promise<{
    uvtFiles: object[];
    uvtFileNames: string[];
    kmMdbFiles: Blob[];
    kmMdbFileNames: string[];
  }> {
    try {
      return await this.decoree.getVehicleTypeFiles(vehicleTypes);
    } catch (error) {
      this.logError('getVehicleTypeFiles', error);

      return {
        uvtFiles: [],
        uvtFileNames: [],
        kmMdbFiles: [],
        kmMdbFileNames: [],
      };
    }
  }

  public async getLatestFileRef(
    assetType: ValidAssetType,
    projectId: string,
    layoutAlternativeId: string,
    revisionId: string,
    vehicleId: string,
  ): Promise<void> {
    try {
      await this.decoree.getLatestFileRef(
        assetType,
        projectId,
        layoutAlternativeId,
        revisionId,
        vehicleId,
      );
    } catch (error) {
      this.logError('getLatestFileRef', error);
    }
  }

  private logError(methodName: string, error: unknown): void {
    logger.error(`Error in ${methodName}:`, error);
  }
}
